import { zod } from '@acme/core'
import { z } from 'zod'
import ApiService from '../api.service'
import { axiosInstance } from '../axios'
import { FuncionarioLocais } from './locais'
import { Registro } from './registros'
import { RegraCalculoPresenter } from './regras-calculo'
import { TabelaHorarios } from './tabela-horarios'

export const funcionarioCentroCustoSchema =
    zod.FuncionarioCentroCustoSchema.omit({
        __typename: true,
        createdAt: true,
        deletedAt: true,
    }).extend({
        CentroCusto: zod.CentroCustoSchema.omit({
            createdAt: true,
            updatedAt: true,
        }).nullish(),
    })

export type FuncionarioCentroCusto = z.infer<
    typeof funcionarioCentroCustoSchema
>

const funcionarioSchema = zod.FuncionarioSchema.omit({
    id: true,
    pontoDiferenciado: true,
    usuarioId: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
    dtRescisao: true,
    matricula: true,
    sexo: true,
    modalidadeContratacao: true,
    funcao: true,
    nome: true,
}).extend({
    id: z.number().optional(),
    matricula: z.coerce.number().positive().nullish(),
    nome: z.coerce.string().trim().min(1, 'Obrigatório'),
    Cidade: zod.CidadeSchema.omit({
        id: true,
        createdAt: true,
        updatedAt: true,
    }).nullish(),
    FuncionarioLocais: zod.FuncionarioLocaisSchema.omit({
        createdAt: true,
    })
        .deepPartial()
        .array()
        .nullish(),
    uf: z.string(),
    pontoDiferenciado: z.string().nullish(),
    sexo: z.enum(['M', 'F']).nullish(),
    modalidadeContratacao: z.string().nullish(),
    dtRescisao: z
        .string()
        .transform(valor =>
            valor &&
            /^(19[0-9][0-9]|20[0-9][0-9]|2100)-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(
                valor,
            )
                ? new Date(`${valor}T03:00:00.000Z`)
                : null,
        )
        .nullish(),
    funcao: z.string().trim().max(100).optional().nullish().default(''),
})

const updateFuncionarioSchema = funcionarioSchema
    .required({
        id: true,
    })
    .extend({
        matricula: z.coerce.number().min(1).max(9999999999),
        FuncionarioCentroCusto: funcionarioCentroCustoSchema
            .deepPartial()
            .array()
            .nullish()
            .or(funcionarioCentroCustoSchema.deepPartial().nullish())
            .refine(
                data => {
                    if (Array.isArray(data))
                        return data.every(item =>
                            item.CentroCusto ? !!item.dtInicial : true,
                        )
                    else if (data?.CentroCusto) return !!data.dtInicial
                    return true
                },
                {
                    message: 'Campo obrigatório',
                    path: ['dtInicial'],
                },
            ),
    })

export type Funcionario = z.infer<typeof updateFuncionarioSchema> & {
    FuncionarioCentroCusto: FuncionarioCentroCusto[]
    FuncionarioLocais: FuncionarioLocais[]
    RegrasCalculo: RegraCalculoPresenter
}
export type CreateFuncionarioInput = z.infer<typeof funcionarioSchema>

const service = new ApiService<
    CreateFuncionarioInput,
    Funcionario & {
        temAjustesPendentes?: boolean
        TabelaHorario?: TabelaHorarios
    }
>('funcionarios')

export type EmpresasFuncionario = {
    id: number
    nomeFantasia: string
}

async function uploadFoto(funcionarioId: number, arquivo: File) {
    const form = new FormData()
    form.append('file', arquivo)

    const res = await axiosInstance.post<{ fotoFilename: string }>(
        `/v1/funcionarios/${funcionarioId}/foto`,
        form,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    )

    return (res.data || res) as unknown as typeof res.data
}

/**
 * Busca a foto do funcionário como object URL. Devolve `null` quando ele não tem
 * foto (404); qualquer outro erro, como uma falha da API de reconhecimento
 * facial, é lançado com a mensagem da API.
 */
async function fetchFotoUrl(funcionarioId: number): Promise<string | null> {
    return axiosInstance
        .get(`/v1/funcionarios/${funcionarioId}/foto`, { responseType: 'blob' })
        .then((blob: any) => URL.createObjectURL(new Blob([blob])))
        .catch(async error => {
            // Com `responseType: 'blob'`, o corpo do erro também chega como Blob.
            const erro =
                error instanceof Blob ? JSON.parse(await error.text()) : error
            if (erro?.statusCode === 404) return null
            throw erro
        })
}

async function deleteFoto(funcionarioId: number) {
    await axiosInstance.delete(`/v1/funcionarios/${funcionarioId}/foto`)
}

type PostBaterPonto = {
    empresaId: number
    funcionarioId?: number
}

export const funcionario = Object.assign(service, {
    ficha: new ApiService<Funcionario>('funcionarios/ficha'),
    relatorio: new ApiService<Funcionario[]>('funcionarios/relatorio-cadastro'),
    exportar: new ApiService<
        { empresaId: number; formato: string; cod?: string },
        { cod: string }
    >('funcionarios/exportar'),
    baterPonto: new ApiService<PostBaterPonto, Registro>(
        'funcionarios/bater-ponto',
    ),
    foto: { upload: uploadFoto, fetchUrl: fetchFotoUrl, remove: deleteFoto },
    aniversariantes: new ApiService<Funcionario[]>(
        'funcionarios/aniversariantes',
    ),
    aniversariantesEmpresa: new ApiService<Funcionario[]>(
        'funcionarios/aniversariantes-empresa',
    ),
})
